import type * as OrtNamespace from "onnxruntime-web";
import { isIos } from "../shared/platform";
import { wasmThreadPolicy } from "./wasm-threads";
import { log, warn } from "../shared/debug";
import { IOS_ORT_DIRECTORY } from "../shared/ort-memory";

type Runtime = typeof OrtNamespace;
const runtimes = new Map<string, Promise<Runtime>>();

export function runtimeThreads() {
  return wasmThreadPolicy(
    location.search,
    navigator.hardwareConcurrency,
    self.crossOriginIsolated,
    isIos() ? 1 : 4,
  );
}

/** CPU work must use the ordinary WASM build, not WebGPU's JSEP/Asyncify runtime.
 * WebKit can exhaust memory compiling the latter even when only CPU OCR is requested.
 * Keep each backend's runtime and tensor constructors together.
 */
export function loadRuntime(backend: string): Promise<Runtime> {
  let pending = runtimes.get(backend);
  if (!pending) {
    pending = (async () => {
      const runtime =
        backend === "webgl"
          ? await import("onnxruntime-web/webgl")
          : backend === "webgpu"
            ? await import("onnxruntime-web/webgpu")
            : await import("onnxruntime-web/wasm");
      const threads = runtimeThreads();
      // Version the loader and WASM together; stable immutable URLs can pair a new JS
      // runtime with an older cached binary on returning phones.
      runtime.env.wasm.wasmPaths = new URL(
        `${import.meta.env.BASE_URL}ort/${runtime.env.versions.common}/${isIos() ? `${IOS_ORT_DIRECTORY}/` : ""}`,
        document.baseURI,
      ).href;
      runtime.env.wasm.numThreads = threads.configured;
      runtime.env.webgl.contextId = "webgl2";
      runtime.env.webgl.textureCacheMode = "initializerOnly";
      runtime.env.webgl.pack = false;
      runtime.env.webgl.async = false;
      runtime.env.logLevel = "error";
      if (threads.invalidOverride)
        warn("model", "ignored invalid ?wasmthreads override; use exactly one of 1, 2, or 4");
      log(
        "model",
        `onnxruntime ${runtime.env.versions.common} ${backend} runtime — WASM threads ${threads.configured}; ${runtime.env.wasm.wasmPaths}`,
      );
      return runtime;
    })();
    runtimes.set(backend, pending);
  }
  return pending;
}
